// DESCRIPTION: Event list page — handles data fetching, pagination state, and error notices.
// DESCRIPTION: Renders the full-page loading/empty/table states for the events admin view.

import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import EventListTable from './EventListTable';

const EventList = ( { onCreateNew, onEditEvent } ) => {
	const [ events, setEvents ] = useState( [] );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ notices, setNotices ] = useState( [] );

	// Pagination state
	const [ currentPage, setCurrentPage ] = useState( 1 );
	const [ itemsPerPage, setItemsPerPage ] = useState( 10 );
	const [ totalItems, setTotalItems ] = useState( 0 );
	const [ totalPages, setTotalPages ] = useState( 1 );

	// Load events with pagination
	const loadEvents = async ( page = currentPage, perPage = itemsPerPage ) => {
		try {
			setIsLoading( true );
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events?page=${ page }&per_page=${ perPage }&status=publish,draft`,
			} );

			// Handle new response format from updated Events API
			if ( response.success && response.result && response.pagination ) {
				setEvents(
					Array.isArray( response.result ) ? response.result : []
				);
				setTotalItems( response.pagination.total_items || 0 );
				setTotalPages( response.pagination.total_pages || 1 );
				setCurrentPage( response.pagination.current_page || page );
				setNotices( [] );
			} else if ( response.success === false ) {
				throw new Error(
					response.message ||
						__( 'Failed to load events.', 'nemtly-booking' )
				);
			} else {
				setEvents( [] );
				setTotalItems( 0 );
				setTotalPages( 1 );
				setNotices( [
					{
						type: 'warning',
						message: __(
							'Unexpected response format from server.',
							'nemtly-booking'
						),
					},
				] );
			}
		} catch ( error ) {
			setNotices( [
				{
					type: 'error',
					message:
						error.message ||
						__( 'Failed to load events.', 'nemtly-booking' ),
				},
			] );
			setEvents( [] );
			setTotalItems( 0 );
			setTotalPages( 1 );
		} finally {
			setIsLoading( false );
		}
	};

	useEffect( () => {
		loadEvents( currentPage, itemsPerPage );
	}, [ currentPage, itemsPerPage ] );

	/**
	 * Handle page change
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} page New page number.
	 */
	const handlePageChange = ( page ) => {
		setCurrentPage( page );
	};

	/**
	 * Handle items per page change
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} perPage New items per page.
	 */
	const handleItemsPerPageChange = ( perPage ) => {
		setItemsPerPage( perPage );
		setCurrentPage( 1 );
	};

	/**
	 * Handle event deletion
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} eventId The event ID to delete.
	 */
	const handleDeleteEvent = async ( eventId ) => {
		if (
			! confirm(
				__(
					'Are you sure you want to delete this event?',
					'nemtly-booking'
				)
			)
		) {
			return;
		}

		try {
			await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }`,
				method: 'DELETE',
			} );

			setNotices( [
				{
					type: 'success',
					message: __(
						'Event deleted successfully.',
						'nemtly-booking'
					),
				},
			] );

			if ( events.length === 1 && currentPage > 1 ) {
				setCurrentPage( currentPage - 1 );
			} else {
				await loadEvents( currentPage, itemsPerPage );
			}
		} catch ( err ) {
			setNotices( [
				{
					type: 'error',
					message:
						err.message ||
						__( 'Failed to delete event.', 'nemtly-booking' ),
				},
			] );
		}
	};

	/**
	 * Refresh events list
	 *
	 * @since PBOOKING_SINCE
	 */
	const handleRefresh = () => {
		loadEvents( currentPage, itemsPerPage );
	};

	const noticeColorMap = {
		success: {
			wrap: 'nb-bg-success-50 nb-border-success-500',
			text: 'nb-text-success-700',
			icon: (
				<svg
					className="nb-w-4 nb-h-4 nb-shrink-0"
					fill="none"
					viewBox="0 0 16 16"
				>
					<circle
						cx="8"
						cy="8"
						r="7"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<path
						d="M5 8l2 2 4-4"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			),
		},
		error: {
			wrap: 'nb-bg-danger-50 nb-border-danger-500',
			text: 'nb-text-danger-700',
			icon: (
				<svg
					className="nb-w-4 nb-h-4 nb-shrink-0"
					fill="none"
					viewBox="0 0 16 16"
				>
					<circle
						cx="8"
						cy="8"
						r="7"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<path
						d="M8 5v3.5M8 10.5v.5"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
				</svg>
			),
		},
		warning: {
			wrap: 'nb-bg-warning-50 nb-border-warning-500',
			text: 'nb-text-warning-700',
			icon: (
				<svg
					className="nb-w-4 nb-h-4 nb-shrink-0"
					fill="none"
					viewBox="0 0 16 16"
				>
					<path
						d="M8 2L14.5 13H1.5L8 2z"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinejoin="round"
					/>
					<path
						d="M8 6.5v3M8 11v.5"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
				</svg>
			),
		},
	};

	if ( isLoading && events.length === 0 ) {
		return (
			<div className="nb-flex nb-flex-col nb-items-center nb-justify-center nb-py-24 nb-gap-4">
				<div className="nb-w-8 nb-h-8 nb-rounded-full nb-border-2 nb-border-neutral-200 nb-border-t-accent-500 nb-animate-spin" />
				<p className="nb-text-sm nb-text-neutral-500">
					{ __( 'Loading events…', 'nemtly-booking' ) }
				</p>
			</div>
		);
	}

	const paginationProps =
		totalPages > 1
			? {
					currentPage,
					totalPages,
					totalItems,
					itemsPerPage,
					onPageChange: handlePageChange,
					onItemsPerPageChange: handleItemsPerPageChange,
			  }
			: null;

	return (
		<div className="nb-event-list nb-flex nb-flex-col">
			{ /* Notices */ }
			{ notices.map( ( notice, index ) => {
				const style =
					noticeColorMap[ notice.type ] || noticeColorMap.warning;
				return (
					<div
						key={ index }
						className={ `nb-flex nb-items-start nb-gap-3 nb-px-4 nb-py-3 nb-rounded-lg nb-border nb-text-sm ${ style.wrap } ${ style.text }` }
					>
						{ style.icon }
						<span className="nb-flex-1">{ notice.message }</span>
						<button
							type="button"
							className="nb-shrink-0 nb-opacity-60 hover:nb-opacity-100 nb-transition-opacity"
							onClick={ () =>
								setNotices( ( prev ) =>
									prev.filter( ( _, i ) => i !== index )
								)
							}
							aria-label={ __( 'Dismiss', 'nemtly-booking' ) }
						>
							<svg
								className="nb-w-4 nb-h-4"
								fill="none"
								viewBox="0 0 16 16"
							>
								<path
									d="M4 4l8 8M12 4l-8 8"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
							</svg>
						</button>
					</div>
				);
			} ) }

			{ /* Content */ }
			<div>
				{ events.length === 0 && ! isLoading ? (
					/* Empty state */
					<div className="nb-flex nb-flex-col nb-items-center nb-justify-center nb-py-20 nb-px-6 nb-text-center nb-bg-neutral-0 nb-rounded-xl nb-border nb-border-neutral-200 nb-shadow-elevation-1">
						<div className="nb-w-14 nb-h-14 nb-rounded-full nb-bg-accent-50 nb-flex nb-items-center nb-justify-center nb-mb-5">
							<svg
								className="nb-w-7 nb-h-7 nb-text-accent-500"
								fill="none"
								viewBox="0 0 24 24"
							>
								<rect
									x="3"
									y="4"
									width="18"
									height="18"
									rx="2"
									stroke="currentColor"
									strokeWidth="1.5"
								/>
								<path
									d="M16 2v4M8 2v4M3 10h18"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
								<path
									d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
								/>
							</svg>
						</div>
						<h3 className="nb-text-base nb-font-semibold nb-text-neutral-900 nb-mb-1">
							{ __( 'No events yet', 'nemtly-booking' ) }
						</h3>
						<p className="nb-text-sm nb-text-neutral-500 nb-mb-6 nb-max-w-xs">
							{ __(
								'Create your first event and start accepting bookings.',
								'nemtly-booking'
							) }
						</p>
						<button
							type="button"
							className="nemtly-button-primary"
							onClick={ onCreateNew }
						>
							{ __(
								'Create Your First Event',
								'nemtly-booking'
							) }
						</button>
					</div>
				) : (
					<EventListTable
						events={ events }
						onDeleteEvent={ handleDeleteEvent }
						onEditEvent={ onEditEvent }
						onRefresh={ handleRefresh }
						onCreateNew={ onCreateNew }
						pagination={ paginationProps }
						isLoading={ isLoading }
					/>
				) }
			</div>
		</div>
	);
};

export default EventList;
